export const COMPARISON_SERIES_DASHARRAY = "6 4";

type LineInterpolationMeta = {
  lineInterpolation?: "linear" | "monotoneX" | null;
};

/**
 * line series の補間方式を field metadata から解決する。
 *
 * @remarks
 * grouped line は synthetic dataKey を使うため、実 field を引けない場合がある。
 * そのときは metricKey へフォールバックし、adapter 側が付与した smoothing metadata を失わない。
 */
export function resolveTimeSeriesLineInterpolation(args: {
  dataKey: string;
  metricKey?: string | null;
  fieldMetaMap: Map<string, LineInterpolationMeta>;
}): "linear" | "monotoneX" {
  const candidateKeys = [
    args.dataKey,
    typeof args.metricKey === "string" ? args.metricKey : null,
  ].filter((key, index, array): key is string => {
    return key !== null && key.length > 0 && array.indexOf(key) === index;
  });

  for (const key of candidateKeys) {
    const interpolation = args.fieldMetaMap.get(key)?.lineInterpolation;
    if (interpolation === "linear" || interpolation === "monotoneX") {
      return interpolation;
    }
  }

  return "linear";
}

export function isComparisonSeriesKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return (
    normalized.startsWith("comparing_") ||
    normalized.startsWith("previous") ||
    normalized.startsWith("compared")
  );
}

export function toComparingDateLabel(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  return trimmed;
}

export function hasComparingDateRows(
  rows: Array<Record<string, unknown>>,
): boolean {
  return rows.some((row) => toComparingDateLabel(row.comparingDate) !== null);
}

function hasAvailableKey(key: string, availableKeys?: Set<string>): boolean {
  if (!availableKeys || availableKeys.size === 0) return true;
  return availableKeys.has(key);
}

function upperFirst(value: string): string {
  if (value.length === 0) return value;
  return value[0]!.toUpperCase() + value.slice(1);
}

export function toCurrentSeriesKey(
  key: string,
  availableKeys?: Set<string>,
): string | null {
  const normalized = key.toLowerCase();
  if (normalized.startsWith("previous") && key.length > "previous".length) {
    const candidate = `current${key.slice("previous".length)}`;
    return hasAvailableKey(candidate, availableKeys) ? candidate : null;
  }
  if (normalized.startsWith("comparing_") && key.length > "comparing_".length) {
    const candidate = key.slice("comparing_".length);
    return hasAvailableKey(candidate, availableKeys) ? candidate : null;
  }
  if (normalized.endsWith("_comparing") && key.length > "_comparing".length) {
    const candidate = key.slice(0, -"_comparing".length);
    return hasAvailableKey(candidate, availableKeys) ? candidate : null;
  }
  return hasAvailableKey(key, availableKeys) ? key : null;
}

export function toComparisonSeriesKey(
  key: string,
  availableKeys?: Set<string>,
): string | null {
  const normalized = key.toLowerCase();

  if (normalized.startsWith("current") && key.length > "current".length) {
    const candidate = `previous${key.slice("current".length)}`;
    if (hasAvailableKey(candidate, availableKeys)) return candidate;
  }

  if (normalized.startsWith("previous") && key.length > "previous".length) {
    const candidate = `comparing_${key.slice("previous".length)}`;
    if (hasAvailableKey(candidate, availableKeys)) return candidate;
  }

  const prefixed = `comparing_${key}`;
  if (hasAvailableKey(prefixed, availableKeys)) return prefixed;

  const suffixed = `${key}_comparing`;
  if (hasAvailableKey(suffixed, availableKeys)) return suffixed;

  const previousByCamel = `previous${upperFirst(key)}`;
  if (hasAvailableKey(previousByCamel, availableKeys)) return previousByCamel;

  return null;
}
